import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useLocation } from 'react-router-dom'
import { Calendar } from 'lucide-react'
import { Button } from "@/components/ui/button"
import { ChatList } from "@/components/chat/ChatList"
import { ChatsSearchBar } from '@/components/chat/ChatsSearchBar'
import { BookingInstructionsDialog } from "@/components/chat/BookingInstructionsDialog"
import { useAuthState } from "@/hooks/useAuth"
import { useUserData } from "@/hooks/useUserData"
import { useNotificationPrompt } from "@/hooks/useNotificationPrompt"

// Update this version when making significant changes to booking instructions
// Artists will see the dialog again when the version changes (unless they previously dismissed)
// This ensures all artists see important updates to the booking flow
// Format: 'v1', 'v2', etc.

/**
 * ChatsPage - Instant Loading ⚡
 * 
 * After the first visit, this page loads INSTANTLY from cache with zero delay.
 * The chat list display data (names, previews, times, badges) is cached to localStorage,
 * allowing the UI to render immediately while fresh data loads in the background.
 * 
 * Performance: First load ~2-3s, subsequent loads ~100-200ms
 */
export default function ChatsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { user, loading: authLoading } = useAuthState()
  const { userData, getCachedProfile, isArtist, loading: userDataLoading } = useUserData()
  const [showInstructionsDialog, setShowInstructionsDialog] = useState(false)
  const [searchQuery, setSearchQuery] = useState("")

  useNotificationPrompt()

  const cachedProfile = getCachedProfile()

  const getDisplayName = () => {
    const displayName = userData?.displayName || cachedProfile?.displayName
    const instagram = userData?.instagram || cachedProfile?.instagram
    const email = user?.email

    if (displayName) {
      return displayName
    }
    if (instagram) {
      return `@${instagram.replace(/^@+/, '')}`
    }
    if (email) {
      return `@${email.split('@')[0]}`
    }
    return 'user'
  }
  
  const onChatsListRoute = location.pathname === '/chats'

  useEffect(() => {
    if (!onChatsListRoute) return
    if (authLoading) return
    if (!user) {
      navigate('/feed', { replace: true })
    }
  }, [authLoading, user, navigate, onChatsListRoute])

  // Redirect non-artists to feed only when they are actually viewing /chats
  useEffect(() => {
    if (!onChatsListRoute) return
    if (!userDataLoading && user && !isArtist) {
      console.log('[ChatsPage] Non-artist user attempting to access chats page, redirecting to feed')
      navigate('/feed', { replace: true })
    }
  }, [user, isArtist, userDataLoading, navigate, onChatsListRoute])

  const handleInstructionsDialogClose = (open: boolean) => {
    setShowInstructionsDialog(open)
  }

  if (authLoading || !user) {
    return <div className="min-h-screen bg-background" />
  }

  return (
    <>
      <div className="flex h-screen-mobile min-h-0 flex-col gap-3 bg-background">
        {/* Title row: safe-area + small cushion */}
        <div className="flex w-full shrink-0 items-start justify-between gap-2 phone-container-padded pt-safe-native-top-cushion md:pt-4">
          <div className="flex flex-1 flex-col min-w-0">
            <h1 className="text-page-title text-foreground">
              {t('chats.title')}
            </h1>
            {user && (
              <p className="text-sm font-medium text-foreground">
                {getDisplayName()}
              </p>
            )}
          </div>
        </div>

        {/* Pinned toolbar: search/booking fixed; list scrolls below */}
        {user && (
          <div className="flex w-full shrink-0 flex-col gap-3 bg-background/95 phone-container-padded backdrop-blur-md supports-[backdrop-filter]:bg-background/80">
            <div className="rounded-lg border border-border bg-card p-3">
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-center gap-2 px-0 py-0.5 text-sm font-medium text-foreground transition-opacity hover:bg-transparent hover:opacity-80"
                onClick={() => setShowInstructionsDialog(true)}
              >
                <Calendar className="size-4 shrink-0 text-foreground" />
                How to book sessions?
              </Button>
            </div>
            <ChatsSearchBar value={searchQuery} onChange={setSearchQuery} placeholder="Search messages..." />
          </div>
        )}

        {/* List scrolls; gap-3 on parent aligns first row with toolbar spacing */}
        <div className="flex min-h-0 min-w-0 w-full flex-1 flex-col overflow-y-auto pb-bottom-nav phone-container-padded [-webkit-overflow-scrolling:touch]">
          <ChatList isArtistView={true} searchQuery={searchQuery} />
        </div>
      </div>

      {/* Booking Instructions Dialog */}
      <BookingInstructionsDialog
        isOpen={showInstructionsDialog}
        onOpenChange={handleInstructionsDialogClose}
      />
    </>
  )
}
